/**
 * CreditBar — AI research credit balance + monthly reset date + top-up button.
 *
 * Reset date is written by the Stripe `invoice.payment_succeeded` webhook
 * (next anchor day). Buy button hits POST /api/billing/credits/topup which
 * returns a Stripe Checkout URL ($5 / 100 credits, max 10 packs per session).
 */

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Coins, Plus } from 'lucide-react'
import { useApi } from '@/hooks/use-api'
import { Button } from '@/components/ui/button'
import { FieldHint } from '@/components/field-hint'

type CreditBalance = {
  readonly balance: number
  readonly total_debited: number
  readonly total_credited: number
  readonly next_reset_at: string | null
}

function formatResetLabel(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

export function CreditBar() {
  const apiFetch = useApi()
  const [topupLoading, setTopupLoading] = useState(false)

  const { data } = useQuery({
    queryKey: ['credits', 'balance'],
    queryFn: async (): Promise<CreditBalance> => {
      const res = await apiFetch<CreditBalance>('/api/leads/credits/balance')
      if (!res.success || !res.data) {
        return { balance: 0, total_debited: 0, total_credited: 0, next_reset_at: null }
      }
      return res.data
    },
    staleTime: 60_000,
  })

  const topupMutation = useMutation({
    mutationFn: async (quantity: number): Promise<{ url: string }> => {
      setTopupLoading(true)
      const res = await apiFetch<{ url: string }>('/api/billing/credits/topup', {
        method: 'POST',
        body: { quantity },
      })
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to start credit purchase')
      }
      return res.data
    },
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: () => {
      setTopupLoading(false)
    },
  })

  const balance = data?.balance ?? 0
  const total = data?.total_credited ?? 100
  const pct = total > 0 ? Math.min(100, (balance / total) * 100) : 0
  const color = pct > 50 ? 'bg-green-500' : pct > 20 ? 'bg-yellow-500' : 'bg-red-500'
  const resetLabel = formatResetLabel(data?.next_reset_at ?? null)

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-2.5" data-tour="discover-credits">
      <Coins className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1 text-muted-foreground">
            AI Research Credits
            <FieldHint label="What are AI Research Credits?">
              Prepaid credits used to unlock preview leads. Each enrich costs one credit. Searching and importing are free.
            </FieldHint>
          </span>
          <span className="font-medium">{balance} remaining</span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
          <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        {resetLabel && (
          <p className="mt-1 text-[10px] text-muted-foreground">
            Resets on {resetLabel}
          </p>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="h-8 shrink-0 text-xs"
        disabled={topupLoading || topupMutation.isPending}
        onClick={() => topupMutation.mutate(1)}
        title="Buy 100 more credits for $5"
      >
        <Plus className="h-3 w-3 mr-1" />
        Buy 100 / $5
      </Button>
    </div>
  )
}
